import type React from 'react'

import { useAuth, usePermissions } from 'commons/customHooks/useAuth'
import { ACTIONS_SCREENS, SCREENS } from 'commons/utils/permissions'
import type { ActionPerm, ScreenPerm } from 'components/Structures/Header/constants'
import { ROUTES, URL_CONST } from 'constants/urls'
import BackofficeRanges from 'container/BackofficeRanges'
import Franchices from 'container/BatchOrder/Franchises'
import International from 'container/BatchOrder/International'
import CreateOrder from 'container/CreateOrder'
import { EdiManagement } from 'container/EdiManagement'
import Error from 'container/Error'
import ErrorElement from 'container/ErrorElement'
import ExtraVolumePage from 'container/ExtraVolumePage/ExtraVolumePage'
import { FairShare } from 'container/FairShare'
import ForgetPassword from 'container/ForgetPassword'
import ChangedPassword from 'container/ForgetPassword/changedPassword'
import ResetPassword from 'container/ForgetPassword/resetPassword'
import ValidateCode from 'container/ForgetPassword/validateCode'
import Home from 'container/Home'
import KeyAccounts from 'container/KeyAccounts'
import LeadTimes from 'container/LeadTimes'
import Login from 'container/Login'
import Main from 'container/Main'
import ManageOrders from 'container/ManageOrders'
import ManageOVDetails from 'container/ManageOVDetails'
import { NDPage } from 'container/NDPage'
import InternationalSampleOrder from 'container/Orders/InternationalSampleOrder'
import NationalSampleOrder from 'container/Orders/NationalSampleOrder'
import ProductPage from 'container/ProductPage'
import { SalesCalendar } from 'container/SalesCalendar'
import SalesOrder from 'container/SalesOrder'
import SalesOrderSimulate from 'container/SalesOrderSimulate'
import { StockReport } from 'container/StockReport'
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom'
import { toast } from 'react-toastify'

interface PrivateProps {
  element: React.ReactElement
  screen: ScreenPerm
  action?: ActionPerm
  shouldVerify?: boolean
}

const Private = ({ element, screen, action, shouldVerify = true }: PrivateProps) => {
  const { isAuthenticated } = useAuth()
  const { verifyPermission } = usePermissions()
  if (!isAuthenticated()) return <Navigate to={URL_CONST.LOGIN} />
  if (shouldVerify) {
    if (verifyPermission(screen, action)) {
      return element
    } else {
      toast.warning('Não possui permissão para acessar esta página', {
        toastId: 'unauthorized',
      })
      return <Navigate to={'/error'} />
    }
  }
  return element
}

const {
  app,
  login,
  forgetPassword,
  orders,
  manage,
  nd,
  stock_report,
  extra_volume,
  backoffice,
  fairShare,
  salesCalendar,
  ediManagement,
} = ROUTES

const routesObject: RouteObject[] = [
  {
    children: [
      {
        element: <Private element={<Home />} screen={SCREENS.HOME} shouldVerify={false} />,
        index: true,
      },
      {
        children: [
          {
            element: <Login />,
            index: true,
          },
        ],
        path: login.base,
      },
      {
        children: [
          { element: <ForgetPassword />, index: true },
          {
            element: <ValidateCode />,
            path: forgetPassword.getLink('validateCode'),
          },
          {
            element: <ResetPassword />,
            path: forgetPassword.getLink('resetPassword'),
          },
          {
            element: <ChangedPassword />,
            path: forgetPassword.getLink('changedPassword'),
          },
        ],
        path: forgetPassword.base,
      },
      { element: <Error />, path: app.getLink('error') },
      {
        children: [
          {
            element: <Private element={<ManageOrders />} screen={SCREENS.MANAGE} />,
            index: true,
          },
          {
            children: [
              {
                element: <Navigate to={manage.base} />,
                path: manage.getLink('order'),
              },
              {
                children: [
                  {
                    element: (
                      <Private
                        action={ACTIONS_SCREENS.SUMMARY}
                        element={<SalesOrder />}
                        screen={SCREENS.ORDER}
                      />
                    ),
                    index: true,
                  },
                  {
                    children: [
                      {
                        element: <Private element={<ProductPage />} screen={SCREENS.OV} />,
                        index: true,
                      },
                    ],
                    path: `${manage.base}${manage.children.order.getLink('orderDetail')}${
                      manage.children.order.children.orderDetail.children.orderMaterial.base
                    }`,
                  },
                ],
                path: `${manage.base}${manage.children.order.getLink('orderDetail')}`,
              },
            ],
            path: manage.getLink('order'),
          },
          {
            children: [
              {
                element: <Navigate to={manage.base} />,
                path: manage.getLink('ov'),
              },
              {
                children: [
                  {
                    element: <Private element={<ManageOVDetails />} screen={SCREENS.OV} />,
                    index: true,
                  },
                  {
                    element: <Private element={<ManageOVDetails />} screen={SCREENS.OV} />,
                    path: `${manage.base}${manage.children.ov.getLink('ovDetail')}${
                      manage.children.ov.children.ovDetail.children.justificar.base
                    }`,
                  },
                ],
                path: `${manage.base}${manage.children.ov.getLink('ovDetail')}`,
              },
            ],
            path: manage.getLink('ov'),
          },
        ],
        path: manage.base,
      },
      {
        children: [
          { element: <Navigate to={manage.base} />, path: orders.base },
          {
            element: (
              <Private
                action={ACTIONS_SCREENS.EDIT}
                element={<CreateOrder />}
                screen={SCREENS.ORDER}
              />
            ),
            path: orders.getLink('new'),
          },
          {
            element: (
              <Private
                action={ACTIONS_SCREENS.PROCESS}
                element={<International />}
                screen={SCREENS.INTERNATIONAL_EDI}
              />
            ),
            path: `${orders.base}${orders.children.batchOrders.getLink('international')}`,
          },
          {
            element: (
              <Private
                action={ACTIONS_SCREENS.SEND}
                element={<Franchices />}
                screen={SCREENS.FRANCHISES_BATCH}
              />
            ),
            path: `${orders.base}${orders.children.batchOrders.getLink('franchises')}`,
          },
          {
            children: [
              {
                element: <Private element={<SalesOrder />} screen={SCREENS.ORDER} />,
                index: true,
                path: orders.getLink('orderDetail'),
              },
              {
                element: (
                  <Private
                    action={ACTIONS_SCREENS.REQUEST}
                    element={<SalesOrderSimulate />}
                    screen={SCREENS.ORDER}
                  />
                ),
                path: `${orders.base}${orders.children.orderDetail.getLink('orderSimulate')}`,
              },
              {
                element: (
                  <Private
                    action={ACTIONS_SCREENS.EDIT}
                    element={<ProductPage />}
                    screen={SCREENS.ORDER}
                  />
                ),
                path: `${orders.base}${orders.children.orderDetail.getLink(
                  'orderMaterials',
                  orders.base,
                )}`,
              },
            ],
            path: orders.getLink('orderDetail'),
          },
          {
            element: <Private element={<NationalSampleOrder />} screen={SCREENS.NATIONAL_SAMPLE} />,
            path: orders.getLink('nationalSampleOrder'),
          },
          {
            element: (
              <Private
                element={<InternationalSampleOrder />}
                screen={SCREENS.INTERNATIONAL_SAMPLE}
              />
            ),
            path: orders.getLink('internationalSampleOrder'),
          },
        ],
        path: orders.base,
      },
      {
        children: [
          {
            element: <Private element={<NDPage />} screen={SCREENS.ND_MANAGE} />,
            index: true,
          },
        ],
        path: nd.base,
      },
      {
        children: [
          {
            element: <Private element={<ExtraVolumePage />} screen={SCREENS.EXTRA_VOLUME} />,
            index: true,
          },
        ],
        path: extra_volume.base,
      },
      {
        children: [
          {
            element: (
              <Private element={<StockReport />} screen={SCREENS.HOME} shouldVerify={false} />
            ),
            index: true,
          },
        ],
        path: stock_report.base,
      },
      {
        children: [
          {
            element: <Private element={<SalesCalendar />} screen={SCREENS.SALES_CALENDAR} />,
            index: true,
          },
        ],
        path: salesCalendar.base,
      },
      {
        children: [
          {
            element: <Navigate to={app.base} />,
            index: true,
            path: backoffice.base,
          },
          {
            element: <Private element={<BackofficeRanges />} screen={SCREENS.BACKOFFICE_RANGE} />,
            path: backoffice.getLink('ranges'),
          },
          {
            element: <Private element={<LeadTimes />} screen={SCREENS.TECHNICAL_CONSULTATION} />,
            path: backoffice.getLink('lead_times'),
          },
          {
            element: <Private element={<KeyAccounts />} screen={SCREENS.TECHNICAL_CONSULTATION} />,
            path: backoffice.getLink('key_accounts'),
          },
        ],
        path: backoffice.base,
      },
      {
        children: [
          {
            element: <Private element={<FairShare />} screen={SCREENS.FAIR_SHARE} />,
            index: true,
          },
        ],
        path: fairShare.base,
      },
      {
        children: [
          {
            element: <Private element={<EdiManagement />} screen={SCREENS.EDI_MANAGEMENT} />,
            index: true,
          },
        ],
        path: ediManagement.base,
      },
      { element: <Navigate to="/error" />, path: '*' },
    ],
    element: <Main />,
    errorElement: <ErrorElement />,
    path: app.base,
  },
]
const router = createBrowserRouter(routesObject)
export default router
